import React, { useEffect, useState } from "react";
import { ITask } from "../../../interfaces/ITask";
import { ITaskPriority } from "../../../interfaces/ITaskPriority";
import { ITaskState } from "../../../interfaces/ITaskState";
import { ITaskType } from "../../../interfaces/ITaskType";
import { IUserSmall } from "../../../interfaces/IUser";
import {
    alpha,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Typography,
    useTheme,
} from "@mui/material";
import { TaskEdit } from "../../../components/task/TaskEdit";
import { IFeedback } from "../../../interfaces/IFeedback";
import { insertIntoArray } from "../../../components/core/generic_helpders";
import { uploadFetchFeedback } from "../../../hooks/useFetch";
import { useNavigate, useParams } from "react-router-dom";


interface IProps {
    taskObject: ITask;
    taskPriorityArray: ITaskPriority[];
    taskStateArray: ITaskState[];
    taskTypeArray: ITaskType[];
    taskArray: ITask[];
    setTaskArray: Function;
    userSmallArray: IUserSmall[];
}

export const TaskView: React.FC<IProps> = ({
    taskObject,
    taskPriorityArray,
    taskStateArray,
    taskTypeArray,
    taskArray,
    setTaskArray,
    userSmallArray,
}) => {
    const params = useParams();
    const navigation = useNavigate();
    const theme = useTheme();

    useEffect(() => {
        if (params.mode === "task" && Number(params.id) === taskObject.id) {
            setEditTask(taskObject);
            setIsOpen(true);
        }
    },[params])

    const currentEditor =  userSmallArray.find((x) => (x.profile === null ? undefined : x.profile.id === taskObject.Editor));
    const currentPriority = taskPriorityArray.find((x) => x.id === taskObject.TaskPriority);
    const currentState = taskStateArray.find((x) => x.id === taskObject.TaskState);

    //
    const [isOpen, setIsOpen] = useState(false);
    const [editTask, setEditTask] = useState(taskObject);
    //
    const [isLoading, setIsLoading] = useState(false);
    const [feedback, setFeedback] = useState<IFeedback>({
        open: false,
        message: "Gespeichert!",
        severity: "success",
    } as IFeedback);

    const handleOpen = () => {
        navigation(`/dashboard/task/${taskObject.id}`)
    };
    const handleClose = () => {
        setIsOpen(false);
        navigation(`/`)
    }

    const handleAfterSave = (newObject: ITask) => {
        setTaskArray(insertIntoArray(newObject, taskArray));
        setEditTask(newObject);
        setIsOpen(false);
    };

    const handleNew = () => {
        uploadFetchFeedback(`/task/${editTask.id}`, false, editTask, handleAfterSave, setIsLoading, setFeedback);
    };

    function stringToColor(string: string) {
        let hash = 0;
        let i;

        /* eslint-disable no-bitwise */
        for (i = 0; i < string.length; i += 1) {
            hash = string.charCodeAt(i) + ((hash << 5) - hash);
        }

        let color = "#";

        for (i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }
        /* eslint-enable no-bitwise */

        return color;
    }

    function stringAvatar(name: string) {
        return {
            sx: {
                bgcolor: stringToColor(name),
                width: 40,
                height: 40,
            },
            children:
                name.length === 0
                    ? ""
                    : name.split(" ").length === 1
                      ? name[0]
                      : `${name.split(" ")[0][0]}${name.split(" ")[1][0]}`,
        };
    }

    return (
        <>
            <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Aufgabe Bearbeiten</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }} />
                    <TaskEdit
                        key={`edit-task-${editTask.id}`}
                        editTask={editTask}
                        setEditTask={setEditTask}
                        taskArray={taskArray}
                        taskPriorityArray={taskPriorityArray}
                        taskStateArray={taskStateArray}
                        taskTypeArray={taskTypeArray}
                        userSmallArray={userSmallArray}
                    />
                </DialogContent>
                <DialogActions>
                    {taskObject.closed_at === null ? (
                        <>
                            <Button variant="outlined" disabled={isLoading} onClick={handleClose}>
                                Abbruch
                            </Button>
                            <Button
                                onClick={handleNew}
                                variant="contained"
                                disabled={isLoading || editTask.title === ""}
                            >
                                Speichern
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button variant="contained" disabled={isLoading} onClick={handleClose}>
                                Okay
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Card variant="outlined" sx={{ cursor: "pointer", mt: 2, mr: 2}} onClick={handleOpen}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ textAlig: "center", width: "100%" }}>
                                {editTask.title}

                                <Box sx={{float: "right"}}>
                                    <Avatar
                                       
                                       {...stringAvatar(`${currentEditor?.first_name} ${currentEditor?.last_name}`)}
                                        alt={`${currentEditor?.first_name} ${currentEditor?.last_name}`}
                                    />
                                </Box>
                            </Typography>

                        </Grid>
                        <Grid item xs={12}>
                            <Chip
                                color={
                                    editTask.TaskState === 1
                                        ? undefined
                                        : [2,5].includes(editTask.TaskState)
                                          ? "success"
                                          : "warning"
                                }
                                variant={
                                    [2,4].includes(editTask.TaskState)
                                        ? "outlined"
                                        : undefined
                                }
                                label={`Status: ${currentState?.taskState}`}
                                sx={{mt: 1}}
                            />
                            <Chip
                                color={
                                    editTask.TaskPriority <= 2
                                        ? undefined
                                        : editTask.TaskPriority === 3
                                          ? "warning"
                                          : "error"
                                }
                                label={`Priorität: ${currentPriority?.taskPriority}`}
                                sx={{mt: 1}}
                            />
                        </Grid>
                        <Grid item xs={6} sx={{mb: -2}}> 
                            <Box
                                sx={{color: theme.palette.grey[500]}}
                            >
                                #{taskObject.id}
                            </Box>
                        </Grid>
                        <Grid item xs={6} sx={{mb: -2}}> 
                            {taskObject.Project !== null &&
                                <Box
                                    sx={{
                                        color: theme.palette.grey[500],
                                        float: "right"
                                    }}
                                >
                                    <Button 
                                        size="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            window.open(`/projects/${taskObject.Project}`, "_blank")
                                        }}
                                    >
                                        Zum Projekt
                                    </Button>
                                </Box>
                            }

                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </>
    );
};
